// src/components/PayPerEventSuccess.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon, 
  CalendarDaysIcon,
  ClockIcon,
  SparklesIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  TicketIcon
} from '@heroicons/react/24/outline';

import SEO from './SEO';
import subscriptionService from '../services/subscriptionService';

const PayPerEventSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  
  const sessionId = searchParams.get('session_id');

  //console.log('🔍 [DEBUG] sessionId from URL:', sessionId);
  //console.log('🔍 [DEBUG] Full URL:', window.location.href);
  //console.log('🔍 [DEBUG] Search params:', Object.fromEntries(searchParams.entries()));
 



  // 🔥 STEP 1: GET USER EMAIL (3 methods with fallback)
  useEffect(() => {
    const fetchEmail = async () => {
      console.log('🔍 Fetching user email...');
      
      // Method 1: Get from URL params
      const emailFromParams = searchParams.get('email');
      
      // Method 2: Get from localStorage (most reliable - stored during checkout)
      const emailFromStorage = subscriptionService.getStoredEmail();
      
      if (emailFromParams) {
        console.log('✅ Email found in URL params:', emailFromParams);
        setUserEmail(emailFromParams);
        setLoading(false);
        return;
      }
      
      if (emailFromStorage) {
        console.log('✅ Email found in localStorage:', emailFromStorage);
        setUserEmail(emailFromStorage);
        setLoading(false);
        return;
      }
      
      // Method 3: Fetch from Stripe session as fallback
      if (sessionId) {
        console.log('⏳ Attempting to fetch email from Stripe session...');
        try {
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-session-info`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ session_id: sessionId })
            }
          );
          
          const data = await response.json();
          
          if (data.success && data.email) {
            console.log('✅ Email fetched from Stripe:', data.email);
            setUserEmail(data.email);
          } else {
            console.warn('⚠️ Could not retrieve email from Stripe session');
            setError('Could not retrieve your email. Please check your inbox for confirmation.');
          }
        } catch (fetchError) {
          console.error('❌ Failed to fetch email from session:', fetchError);
          setError('Could not retrieve your email. Please check your inbox for confirmation.');
        }
      } else {
        console.warn('⚠️ No session ID available');
        setError('Invalid session. Please contact support if you need assistance.');
      }
      
      setLoading(false);
    };

    fetchEmail();
  }, [searchParams, sessionId]);


  // 🔥 STEP 2: SEND CONFIRMATION EMAIL (triggers after email is available)
useEffect(() => {
  const sendConfirmationEmail = async () => {
    if (!userEmail || !sessionId) {
      console.log('⏭️ Skipping email send - missing email or sessionId');
      return;
    }

    if (emailSent) {
      console.log('⏭️ Email already sent, skipping');
      return;
    }

    console.log('📧 Sending pass confirmation email to:', userEmail);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-signup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`, // 🔥 ADD THIS
          },
          body: JSON.stringify({
            type: 'pass_confirmation',
            email: userEmail,
            pass_details: {
              amount: 600,
              currency: 'JPY',
              session_id: sessionId,
              purchased_at: new Date().toISOString(),
              expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
            }
          })
        }
      );

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Confirmation email sent successfully');
        setEmailSent(true);
      } else {
        console.error('❌ Failed to send confirmation email:', result.error);
      }
    } catch (emailError) {
      console.error('❌ Error sending confirmation email:', emailError);
    }
  };

  sendConfirmationEmail();
}, [userEmail, sessionId, emailSent]);





  // SEO Configuration
  const seoConfig = {
    title: "Payment Successful - Event Pass Purchased | ConnectCo",
    description: "Your ConnectCo event pass has been purchased successfully. Check your email for confirmation and next steps.",
    url: "https://connectco.app/pay-per-event-success",
    noindex: true // Don't index success pages
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-20">
        <SEO {...seoConfig} />
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Processing your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-12 pt-28 md:py-16 md:pt-32">
      <SEO {...seoConfig} />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircleIcon className="w-12 h-12 text-green-600" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            🎉 Payment Successful!
          </h1>
          
          <p className="text-lg md:text-xl text-gray-700 mb-2">
            Your event pass has been purchased
          </p>
          
          {userEmail && (
            <p className="text-sm md:text-base text-gray-600 flex items-center justify-center">
              <EnvelopeIcon className="w-5 h-5 mr-2" />
              Confirmation sent to: <strong className="ml-1">{userEmail}</strong>
            </p>
          )}
        </motion.div>

        {/* Error Message (if any) */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-center"
          >
            <p className="text-yellow-800 text-sm">{error}</p>
          </motion.div>
        )}

        {/* Pass Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6"
        >
          {/* Pass Ticket Design */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <TicketIcon className="w-8 h-8 mr-3" />
                <div>
                  <p className="text-sm opacity-90">Single Event Pass</p>
                  <p className="text-2xl font-bold">¥900</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-90">Valid for</p>
                <p className="text-xl font-bold">90 Days</p>
              </div>
            </div>
          </div>

          {/* Pass Details */}
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Your Pass Details</h3>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Access to Any Single Event</p>
                  <p className="text-sm text-gray-600">Choose from Weekly Signature Fika, Monthly Fika, Speed Fika, Happy Hours, and more</p>
                </div>
              </div>

              <div className="flex items-start">
                <ClockIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Valid for 90 Days</p>
                  <p className="text-sm text-gray-600">
                    Use your pass before: <strong>{new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <CalendarDaysIcon className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Easy Registration</p>
                  <p className="text-sm text-gray-600">When you RSVP for an event, we'll automatically apply your pass</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Next Steps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6"
        >
          <h3 className="text-lg font-bold text-blue-900 mb-4">📋 Next Steps</h3>
          
          <ol className="space-y-3">
            <li className="flex items-start">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 text-sm font-bold">1</span>
              <div>
                <p className="font-semibold text-blue-900">Check Your Email</p>
                <p className="text-sm text-blue-700">We've sent a confirmation with your pass details to {userEmail || 'your email'}</p>
              </div>
            </li>

            <li className="flex items-start">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 text-sm font-bold">2</span>
              <div>
                <p className="font-semibold text-blue-900">Browse Upcoming Events</p>
                <p className="text-sm text-blue-700">Explore our event calendar and find the perfect event for you</p>
              </div>
            </li>

            <li className="flex items-start">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mr-3 text-sm font-bold">3</span>
              <div>
                <p className="font-semibold text-blue-900">RSVP for an Event</p>
                <p className="text-sm text-blue-700">Your pass will be automatically applied during checkout</p>
              </div>
            </li>
          </ol>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => navigate('/events')}
            className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all hover:scale-105 flex items-center justify-center"
          >
            <CalendarDaysIcon className="w-5 h-5 mr-2" />
            Browse Events
            <ArrowRightIcon className="w-5 h-5 ml-2" />
          </button>

          <button
            onClick={() => navigate('/subscription')}
            className="flex-1 bg-white text-green-600 border-2 border-green-600 py-4 px-6 rounded-lg font-semibold hover:bg-green-50 transition-all flex items-center justify-center"
          >
            <SparklesIcon className="w-5 h-5 mr-2" />
            Upgrade to Unlimited
          </button>
        </motion.div>

        {/* Important Notes */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 bg-gray-50 border border-gray-200 rounded-xl p-6"
        >
          <h3 className="text-base font-bold text-gray-900 mb-3">📌 Important Notes</h3>
          
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="text-green-600 mr-2">•</span>
              <span><strong>Non-refundable:</strong> Event passes cannot be refunded once purchased</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">•</span>
              <span><strong>Single use:</strong> Your pass grants access to one event only</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">•</span>
              <span><strong>90-day validity:</strong> Pass must be used within 90 days of purchase</span>
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">•</span>
              <span><strong>Automatic application:</strong> Pass will be applied automatically when you RSVP using the same email</span>
            </li>
          </ul>

          <div className="mt-4 pt-4 border-t border-gray-300">
            <p className="text-sm text-gray-600">
              Need help? Contact us at{' '}
              <a href="mailto:hello@connectco.app" className="text-green-600 hover:text-green-700 font-semibold">
                hello@connectco.app
              </a>
              {' '}or visit our{' '}
              <Link to="/contact" className="text-green-600 hover:text-green-700 font-semibold">
                contact page
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Pro Tip */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 text-center"
        >
          <p className="text-purple-900 font-semibold mb-2">💡 Pro Tip</p>
          <p className="text-purple-800 text-sm mb-4">
            Regular attendee? Save more with a subscription! Get unlimited access to all events and curated fika sessions for just ¥2,100/month.
          </p>
          <button
            onClick={() => navigate('/subscription')}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all text-sm"
          >
            See Subscription Plans
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default PayPerEventSuccess;